import React from "react";
import { gql } from "@apollo/client";
import { useForm } from "react-final-form";
import { createIntl, createIntlCache } from "react-intl";
import { FieldArray } from "react-final-form-arrays";
import { Fieldset, TextField } from "@syos-music/form";
import { Product } from "@syos-music/products";

import { Button, Grid, IconButton, InputAdornment } from "@mui/material";
import { Delete as DeleteIcon, Plus as AddIcon } from "mdi-material-ui";

import PriceField from "components/form/PriceField";

const enIntl = createIntl(
  {
    locale: "en",
    messages: {
      ...require("@syos-music/form/lang/en.json"),
      ...require("@syos-music/instruments/lang/en.json"),
      ...require("@syos-music/products/lang/en.json"),
    },
  },
  createIntlCache()
);

export default function ShipmentContentField({ name, order }) {
  const form = useForm();

  const formatInteger = React.useCallback((val) => {
    const s = typeof val === "number" ? val.toFixed(0) : val;
    return s ?? "";
  }, []);
  const handleBlurInteger = React.useCallback(
    (e) => {
      if (!e.target.value) return;
      const value = parseInt(e.target.value);
      form.change(e.target.name, value);
    },
    [form]
  );

  return (
    <Fieldset legend="Contenu du colis">
      <FieldArray name={name}>
        {({ fields }) => (
          <Grid container>
            {fields.map((fieldName, idx) => (
              <Grid key={idx} item xs={12}>
                <Grid container wrap="nowrap">
                  <Grid item xs>
                    <TextField
                      name={`${fieldName}.description`}
                      required
                      label="Description"
                    />
                  </Grid>

                  <Grid item xs={2}>
                    <PriceField
                      name={`${fieldName}.price`}
                      required
                      label="Prix"
                    />
                  </Grid>

                  <Grid item xs={2}>
                    <TextField
                      name={`${fieldName}.weight`}
                      type="number"
                      format={formatInteger}
                      onBlur={handleBlurInteger}
                      required
                      label="Poids"
                      InputProps={{
                        inputProps: { min: 0, step: 1 },
                        endAdornment: (
                          <InputAdornment position="end">g</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={2}>
                    <TextField
                      name={`${fieldName}.quantity`}
                      type="number"
                      format={formatInteger}
                      onBlur={handleBlurInteger}
                      required
                      label="Qté"
                      InputProps={{
                        inputProps: { min: 1, step: 1 },
                        startAdornment: (
                          <InputAdornment position="start">
                            &times;
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item>
                    <IconButton
                      onClick={() => fields.remove(idx)}
                      size="small"
                      sx={{ mt: 2 }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Grid>
            ))}

            <Grid item xs={12}>
              <Button
                onClick={() =>
                  fields.push({
                    price: { currency: order.currency, value: 0 },
                    quantity: 1,
                  })
                }
                startIcon={<AddIcon />}
                size="small"
                variant="text"
              >
                Ajouter une ligne
              </Button>
            </Grid>
          </Grid>
        )}
      </FieldArray>
    </Fieldset>
  );
}

ShipmentContentField.fragments = {
  order: gql`
    fragment ShipmentContentField_Order on Order {
      id

      currency

      cart {
        id
        product {
          id
          displayMode
          label {
            en
            fr
          }
          defaultOptions
        }
        options
        quantity
        unitPrice {
          currency
          value
        }
        price {
          currency
          value
        }
      }
    }
  `,
};

ShipmentContentField.getInitialValue = (order) => {
  const expandCartItem = (cartItem) => {
    let instrument;
    let instrumentType;

    switch (cartItem.product.displayMode) {
      case "customSaxophoneMouthpiece":
      case "signatureSaxophoneMouthpiece":
        instrument = "saxophone";
        instrumentType = cartItem.options.saxophoneType;
        break;

      case "customClarinetMouthpiece":
      case "signatureClarinetMouthpiece":
        instrument = "clarinet";
        instrumentType = cartItem.options.clarinetType;
        break;

      case "originalsMouthpiece":
      case "signatureMouthpiece":
        if (!cartItem.options.addLigature) return [cartItem];

        instrument = cartItem.product.defaultOptions.instrument;
        instrumentType = cartItem.options.instrumentType;
        break;

      case "customMouthpiece":
        instrument = cartItem.product.defaultOptions.instrument;
        instrumentType = cartItem.options.instrumentType;
        break;

      default:
        return [cartItem];
    }

    return [
      cartItem,
      {
        product: {
          displayMode: "mouthpieceLigature",
          label: { en: "Mouthpiece ligature" },
          defaultOptions: { instrument },
        },
        options: { instrumentType },
        quantity: cartItem.quantity,
        unitPrice: { currency: cartItem.unitPrice.currency, value: 0 },
      },
      {
        product: {
          displayMode: "mouthpieceCap",
          label: { en: "Mouthpiece cap" },
        },
        options: { instrument, instrumentType },
        quantity: cartItem.quantity,
        unitPrice: { currency: cartItem.unitPrice.currency, value: 0 },
      },
    ];
  };

  return order.cart
    .reduce((acc, cartItem) => acc.concat(expandCartItem(cartItem)), [])
    .filter(Boolean)
    .map((cartItem) => {
      const description = Product.Label.asString(
        enIntl,
        cartItem.product,
        cartItem.options
      );

      const weight = Product.getWeight(cartItem.product, cartItem.options) ?? 0;

      return {
        description,
        weight,
        price: cartItem.unitPrice,
        quantity: cartItem.quantity,
      };
    });
};
